import { Settings, RefreshCw, Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import LogoutButton from '../../../components/dispacer/LogoutDispacer';

interface HeaderProps {
  currentTime: Date;
  activeSection: string;
  connectionStatus: 'connected' | 'connecting' | 'error';
  lastRefresh?: Date;
  shipments: any[];
  loading: boolean;
  isRefreshing: boolean;
  token: string | null;
  error: string | null;
  refreshData: (force?: boolean) => void;
  getConnectionIcon: () => React.ReactNode;
}

const Header = ({
  currentTime,
  activeSection,
  connectionStatus,
  lastRefresh,
  shipments,
  loading,
  isRefreshing,
  token,
  error,
  refreshData,
  getConnectionIcon
}: HeaderProps) => {

  const getSectionInfo = (section: string) => {
    switch (section) {
      case 'dashboard':
        return {
          title: 'Dashboard Principal',
          description: 'Monitorizare și coordonare transport în timp real'
        };
      case 'expedieri':
        return {
          title: 'Gestionare Expedieri',
          description: 'Administrare și urmărire expedieri active'
        };
      case 'expedieri-active':
        return {
          title: 'Expedieri Active',
          description: 'Vizualizare expedieri în desfășurare'
        };
      case 'expedieri-finalizate':
        return {
          title: 'Expedieri Finalizate',
          description: 'Istoric și analiză expedieri completate'
        };
      case 'soferi':
        return {
          title: 'Management Șoferi',
          description: 'Administrare echipă și asignare vehicule'
        };
      case 'calendar':
        return {
          title: 'Programări & Calendar',
          description: 'Planificare și vizualizare activități'
        };
      case 'rapoarte':
        return {
          title: 'Rapoarte Operaționale',
          description: 'Analiză performanță și documentație CMR'
        };
      default:
        return {
          title: 'Centru de Control Dispecerat',
          description: 'Monitorizare și coordonare transport în timp real'
        };
    }
  };

  const sectionInfo = getSectionInfo(activeSection);

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-start flex-wrap gap-4">
          
          {/* Secțiunea titlu */}
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{sectionInfo.title}</h1>
            <p className="text-sm text-gray-500">{sectionInfo.description}</p>
          </div>

          {/* Status, refresh și ora */}
          <div className="flex items-center gap-6 flex-wrap">
            {/* Status conexiune */}
           

            {/* Ultima actualizare */}
            {lastRefresh && (
              <div className="text-sm text-gray-500">
                Ultima actualizare: {lastRefresh.toLocaleTimeString('ro-RO')}
              </div>
            )}

            {/* Număr expedieri */}
            {!loading && (
              <div className="text-sm text-gray-600">
                {shipments.length} expedieri
              </div>
            )}

            {/* Buton actualizare */}
            <button
              onClick={() => refreshData(true)}
              disabled={isRefreshing || !token}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200
                ${isRefreshing || !token
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg transform hover:scale-105'
                }`}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>{isRefreshing ? 'Actualizează' : 'Actualizează'}</span>
            </button>

            {/* Ora curentă */}
            <div className="text-right bg-gray-50 px-4 py-2 rounded-lg">
              <div className="text-xs text-gray-500">Ora curentă</div>
              <div className="text-sm font-semibold text-gray-900">
                {currentTime.toLocaleTimeString('ro-RO')}
              </div>
              <div className="text-xs text-gray-600">
                {currentTime.toLocaleDateString('ro-RO', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {/* Setări și Logout */}
            
            <LogoutButton />
          </div>
        </div>
      </header>

     
     

      {/* Enhanced Alert Messages - moved from main component */}
      <div className="px-6 py-2 space-y-2">
        {/* Authentication Warning */}
        {!token && (
          <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 rounded-r-lg shadow-sm">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <p className="font-semibold text-yellow-800">Avertizare de Autentificare</p>
                <p className="text-yellow-700 text-sm">
                  Nu sunteți autentificat. Vă rugăm să vă conectați pentru a accesa toate funcționalitățile.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Global Error */}
        {error && (
          <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-400 rounded-r-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
                <div>
                  <p className="font-semibold text-red-800">Eroare</p>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
              <button 
                onClick={() => refreshData(true)}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
                disabled={!token}
              >
                Reîncearcă
              </button>
            </div>
          </div>
        )}

        {/* Empty Data Info */}
        {!loading && !error && shipments.length === 0 && token && (
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-400 rounded-r-lg shadow-sm">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-blue-600 mr-3" />
              <div>
                <p className="font-semibold text-blue-800">Informație</p>
                <p className="text-blue-700 text-sm">
                  Nu există expedieri în baza de date.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Header;